import { AnnouncementPicture } from '@/components/AnnouncementPicture';
import { Modal } from '@/components/ui';
import { useNow } from '@/hooks/useNow';
import { usePreferences } from '@/stores/preferences';
import { NoticeMeta, PriorityTag } from './AnnouncementParts';
import { localize, type FeedItem } from './announcementFeed';

const MINUTE = 60_000;

/**
 * One announcement in full, opened from the ticker or the board.
 *
 * `live` is false once the notice has left the feed while open — it ended,
 * or was taken down. The pop-up then keeps
 * what the person was reading and says so, rather than vanishing under them.
 */
export function AnnouncementDialog({
  item,
  live,
  onClose,
}: {
  item: FeedItem;
  live: boolean;
  onClose: () => void;
}) {
  const language = usePreferences((s) => s.prefs.language);
  const now = useNow(MINUTE);
  const text = localize(item, language);

  return (
    <Modal title={text.headline} onClose={onClose} medium={!!item.imageKey} className="noticedialog">
      {/* The Modal's title only names the dialog for screen readers; the
          headline people read is drawn here. */}
      <header className="noticedialog__head">
        <PriorityTag priority={item.priority} />
        <h2 className="noticedialog__headline" dir="auto">
          {text.headline}
        </h2>
      </header>

      {!live && (
        <p className="noticedialog__ended" role="status">
          This announcement is no longer live.
        </p>
      )}

      {text.body && (
        <p className="noticedialog__body" dir="auto">
          {text.body}
        </p>
      )}

      <AnnouncementPicture
        id={item.id}
        imageKey={item.imageKey}
        alt={text.headline}
        className="announcementpicture--full"
      />

      <NoticeMeta item={item} author={text.author} now={now} />
    </Modal>
  );
}
